import { Injectable, inject , signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { UnitSettings } from '../../models/unit-settings';
import { MapSettings } from '../../models/map-settings';
import { RoutingSettings } from '../../models/routing-settings';
import { VoiceGuidenceSettings } from '../../models/voice-guidence-settings';
import { Address } from '../../models/address';
import { HttpService } from '../core/http.service';

export const enum Unit {
  Metric = "metric",
  Imperial = "imperial"
}

/**
 * Asset family. Doubles as a path segment (`assets/icons/<skin>/...`) and as the
 * light-vs-dark switch every map drawer branches on, so it stays two-valued.
 * Themes beyond light/dark resolve onto one of these two - see MapTheme.
 */
export const enum MapSkin {
  Light = "light",
  Dark = "dark"
}

/** Palette the user picks in Settings. Each one resolves to a MapSkin for assets. */
export const enum MapTheme {
  Light = "light",
  Dark = "dark",
  /** BMW cabin palette: BMW blue on a near-black cool grey, over the dark asset set. */
  BMW = "bmw"
}

/** Body class carrying a theme's palette. Themes with no entry use the default (dark). */
const THEME_BODY_CLASS: Partial<Record<MapTheme, string>> = {
  [MapTheme.Light]: "lightSkin",
  [MapTheme.BMW]: "bmwSkin"
};

/** Every class THEME_BODY_CLASS can set, so switching theme can clear the previous one. */
const ALL_THEME_BODY_CLASSES: string[] = Object.values(THEME_BODY_CLASS) as string[];

/** The asset/basemap family a theme draws from. */
export function skinForTheme(theme: MapTheme): MapSkin {
  return theme === MapTheme.Light ? MapSkin.Light : MapSkin.Dark;
}

/** Parses a stored theme string; anything unknown falls back to `fallback`. */
export function parseTheme(value: unknown, fallback: MapTheme): MapTheme {
  switch (String(value ?? "").toLowerCase()) {
    case MapTheme.Light: return MapTheme.Light;
    case MapTheme.Dark: return MapTheme.Dark;
    case MapTheme.BMW: return MapTheme.BMW;
    default: return fallback;
  }
}

export const enum AddressType {
  Home = "home",
  Work = "work"
}

export const Language = [
  { "code": "de", "name": "Deutsch" },
  { "code": "en", "name": "English" },
  { "code": "es", "name": "Español" },
  { "code": "fr", "name": "Français" },
  { "code": "it", "name": "Italiano" },
  { "code": "nl", "name": "Nederlands" },
  { "code": "no", "name": "Norsk" },
  { "code": "se", "name": "Svenska" }
]

export const UNIT_METRIC: UnitSettings = { Distance: Unit.Metric, Temperature: Unit.Metric, Speed: Unit.Metric, Pressure: Unit.Metric };
export const UNIT_IMPERIAL: UnitSettings = { Distance: Unit.Imperial, Temperature: Unit.Imperial, Speed: Unit.Imperial, Pressure: Unit.Imperial };
export const MAP_DEFAULT: MapSettings = { Skin: MapSkin.Light, Theme: MapTheme.Light, POIs: false };
export const ROUTING_DEFAULT: RoutingSettings = { TollRoads: true, Ferries: true, Highways: true };
export const VOICEGUIDENCE_DEFAULT: VoiceGuidenceSettings = { Mute: false };
export const VOICEINPUTTUTORIALDONE_DEFAULT: boolean = false;

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private isAuthorized: boolean = false;
  private readonly _unit = signal<UnitSettings>(undefined as unknown as UnitSettings);
  public ObservableUnit = new BehaviorSubject<UnitSettings | null>(null);
  private Routing!: RoutingSettings;
  public ObservableRoutingSettings = new BehaviorSubject<RoutingSettings | null>(null);
  private VoiceGuidence!: VoiceGuidenceSettings;
  private readonly _map = signal<MapSettings>(undefined as unknown as MapSettings);
  public ObservableMap = new BehaviorSubject<MapSettings | null>(null);
  private VoiceInputTutorialDone!: boolean;
  private Addresses: Address[] = [];
  private DisabledChargingStationIds: string[] = [];
  private FavoriteChargingStationIds: string[] = [];
  private DisabledChargingOperatorIds: number[] = [];
  private FavoriteChargingOperatorIds: number[] = [];
  private Tracking: boolean = true;
  public ObservableTracking = new BehaviorSubject<boolean>(true);
  public acceptCookies: boolean = true;

  private readonly httpService = inject(HttpService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);

  constructor() {
    this.initSettings();
  }

  // load settings from localstorage
  // set default values if the localstorage values is empty
  public initSettings(): void {
    this.Addresses = [];

    let unitSettings, routingSettings, voiceguidenceSettings, mapSettings, voiceInputTutorialDoneSettings,
      disabledChargingStationsSettings, favoriteChargingStationsSettings, disabledChargingOperatorsSettings,
      favoriteChargingOperatorsSettings;
    try {
      unitSettings = JSON.parse(localStorage.getItem("unit")!);
    } catch (e) { }
    if (typeof (unitSettings) == 'object' && unitSettings != null) {
      this.setUnit(this.unitJSONtoUnitSettings(unitSettings));
    }
    else if (unitSettings === "metric") {
      this.setUnit(UNIT_METRIC);
    }
    else if (unitSettings === "imperial") {
      this.setUnit(UNIT_IMPERIAL);
    }
    else {
      this._unit.set(UNIT_METRIC);

      this.httpService.getCheck().subscribe((resp) => {
        if (resp.substring(0, 2) == "GB" || resp.substring(0, 2) == "US") {
          this.setUnit(UNIT_IMPERIAL);
        }
      });
    }

    try {
      routingSettings = JSON.parse(localStorage.getItem("routing")!);
    } catch (e) { }
    if (typeof (routingSettings) == 'object' && routingSettings != null) {
      this.setRounting(this.routingJSONtoRoutingSettings(routingSettings));
    } else {
      this.setRounting(ROUTING_DEFAULT);
    }

    try {
      voiceguidenceSettings = JSON.parse(localStorage.getItem("voiceguidence")!);
    } catch (e) { }
    if (typeof (voiceguidenceSettings) == 'object' && voiceguidenceSettings != null) {
      this.setVoiceGuidence(this.voiceGuidenceJSONtoVoiceGuidenceSettings(voiceguidenceSettings))
    } else {
      this.setVoiceGuidence(VOICEGUIDENCE_DEFAULT);
    }

    try {
      mapSettings = JSON.parse(localStorage.getItem("mapsettings")!);
    } catch (e) { }
    if (typeof (mapSettings) == 'object' && mapSettings != null) {
      this.setMap(this.mapJSONtoMapSettings(mapSettings));
    } else {
      this.setMap(MAP_DEFAULT);
    }

    try {
      voiceInputTutorialDoneSettings = JSON.parse(localStorage.getItem("voiceInputTutorialDone")!);
    } catch (e) { }
    if (voiceInputTutorialDoneSettings != null) {
      this.setVoiceInputTutorialDone(voiceInputTutorialDoneSettings);
    } else {
      this.setVoiceInputTutorialDone(VOICEINPUTTUTORIALDONE_DEFAULT);
    }

    try {
      disabledChargingStationsSettings = JSON.parse(localStorage.getItem("disabledChargingStationsEM")!);
    } catch (e) { }
    if (disabledChargingStationsSettings != null) {
      this.setDisabledChargingStationIds(disabledChargingStationsSettings);
    }

    try {
      favoriteChargingStationsSettings = JSON.parse(localStorage.getItem("favoriteChargingStationsEM")!);
    } catch (e) { }
    if (favoriteChargingStationsSettings != null) {
      this.setFavoriteChargingStationIds(favoriteChargingStationsSettings);
    }

    try {
      disabledChargingOperatorsSettings = JSON.parse(localStorage.getItem("disabledChargingOperatorsEM")!);
    } catch (e) { }
    if (disabledChargingOperatorsSettings != null) {
      this.setDisabledChargingOperatorIds(disabledChargingOperatorsSettings.map(Number));
    }

    try {
      favoriteChargingOperatorsSettings = JSON.parse(localStorage.getItem("favoriteChargingOperatorsEM")!);
    } catch (e) { }
    if (favoriteChargingOperatorsSettings != null) {
      this.setFavoriteChargingOperatorIds(favoriteChargingOperatorsSettings.map(Number));
    }
  }

  public setIsAuthorized(isAuthorized: boolean): void {
    this.isAuthorized = isAuthorized;
  }

  /** Reading this from a template registers the view as a consumer of the unit signal. */
  public getUnit(): UnitSettings {
    return this._unit();
  }

  public setUnit(unit: UnitSettings) {
    this._unit.set(unit);
    if (!this.isAuthorized) {
      localStorage.setItem("unit", JSON.stringify(unit).toLowerCase());
    }
    this.ObservableUnit.next(unit);
  }

  public unitJSONtoUnitSettings(unitJSON): UnitSettings {
    return {
      Distance: unitJSON.distance === "imperial" ? Unit.Imperial : Unit.Metric,
      Temperature: unitJSON.temperature === "imperial" ? Unit.Imperial : Unit.Metric,
      Speed: unitJSON.speed === "imperial" ? Unit.Imperial : Unit.Metric,
      Pressure: unitJSON.pressure === "imperial" ? Unit.Imperial : Unit.Metric,
    }
  }

  public getRouting(): RoutingSettings {
    return this.Routing;
  }

  public setRounting(routing: RoutingSettings) {
    this.Routing = routing;
    if (!this.isAuthorized) {
      localStorage.setItem("routing", JSON.stringify(routing).toLowerCase());
    }
    this.ObservableRoutingSettings.next(routing);
  }

  public routingJSONtoRoutingSettings(routingJSON): RoutingSettings {
    return {
      TollRoads: routingJSON.tollroads,
      Ferries: routingJSON.ferries,
      Highways: routingJSON.highways
    }
  }

  public getVoiceGuidence(): VoiceGuidenceSettings {
    return this.VoiceGuidence;
  }

  public setVoiceGuidence(voiceguidence: VoiceGuidenceSettings): void {
    this.VoiceGuidence = voiceguidence;
    if (!this.isAuthorized) {
      localStorage.setItem("voiceguidence", JSON.stringify(voiceguidence).toLowerCase());
    }
  }

  public voiceGuidenceJSONtoVoiceGuidenceSettings(voiceGuidenceJSON): VoiceGuidenceSettings {
    return {
      Mute: voiceGuidenceJSON.mute,
    }
  }

  /** Reading this from a template registers the view as a consumer of the map signal. */
  public getMap(): MapSettings {
    return this._map();
  }

  public setMap(map: MapSettings): void {
    this._map.set(map);
    if (!this.isAuthorized) {
      localStorage.setItem("mapsettings", JSON.stringify(map).toLowerCase());
    }
    const themeClass = THEME_BODY_CLASS[map.Theme];
    document.body.classList.remove(...ALL_THEME_BODY_CLASSES.filter(cls => cls !== themeClass));
    if (themeClass) {
      document.body.classList.add(themeClass);
    }
    this.ObservableMap.next(map);
  }

  /**
   * `theme` is the authority. `skin` is the fallback, for settings written before themes
   * existed and for a backend that drops the key it does not know - those degrade to
   * plain dark rather than to light.
   */
  public mapJSONtoMapSettings(mapJSON): MapSettings {
    const skinFallback = mapJSON.skin === "light" ? MapTheme.Light : MapTheme.Dark;
    const theme = parseTheme(mapJSON.theme, skinFallback);
    return {
      Skin: skinForTheme(theme),
      Theme: theme,
      POIs: mapJSON.pois
    }
  }

  public getVoiceInputTutorialDone(): boolean {
    return this.VoiceInputTutorialDone;
  }

  public setVoiceInputTutorialDone(voiceInputTutorialDone): void {
    this.VoiceInputTutorialDone = voiceInputTutorialDone;
    if (!this.isAuthorized) {
      localStorage.setItem("voiceInputTutorialDone", String(voiceInputTutorialDone));
    }
  }

  public getDisabledChargingOperatorIds(): number[] {
    return this.DisabledChargingOperatorIds;
  }

  public getContainsDisabledChargingOperatorId(chargingOperatorId: number): boolean {
    return this.DisabledChargingOperatorIds.includes(chargingOperatorId);
  }

  public setDisabledChargingOperatorIds(disabledChargingOperatorsList: number[]): void {
    this.DisabledChargingOperatorIds = disabledChargingOperatorsList;
    if (!this.isAuthorized) {
      localStorage.setItem("disabledChargingOperatorsEM", JSON.stringify(disabledChargingOperatorsList));
    }
  }

  public getFavoriteChargingOperatorIds(): number[] {
    return this.FavoriteChargingOperatorIds;
  }

  public getContainsFavoriteChargingOperatorId(chargingOperatorId: number): boolean {
    return this.FavoriteChargingOperatorIds.includes(chargingOperatorId);
  }

  public setFavoriteChargingOperatorIds(favoriteChargingOperatorsList: number[]): void {
    this.FavoriteChargingOperatorIds = favoriteChargingOperatorsList;
    if (!this.isAuthorized) {
      localStorage.setItem("favoriteChargingOperatorsEM", JSON.stringify(favoriteChargingOperatorsList));
    }
  }

  public getDisabledChargingStationIds(): string[] {
    return this.DisabledChargingStationIds;
  }

  public getContainsDisabledChargingStationId(chargingStationId: string): boolean {
    const disabledIndex = this.DisabledChargingStationIds.indexOf(chargingStationId);

    return disabledIndex != -1;
  }

  public setDisabledChargingStationIds(disabledChargingStationList: string[]): void {
    this.DisabledChargingStationIds = disabledChargingStationList;
    if (!this.isAuthorized) {
      localStorage.setItem("disabledChargingStationsEM", JSON.stringify(disabledChargingStationList));
    }
  }

  public getFavoriteChargingStationIds(): string[] {
    return this.FavoriteChargingStationIds;
  }

  public getContainsFavoriteChargingStationId(chargingStationId: string): boolean {
    const favoriteIndex = this.FavoriteChargingStationIds.indexOf(chargingStationId);

    return favoriteIndex != -1;
  }

  public setFavoriteChargingStationIds(favoriteChargingStationList: string[]): void {
    this.FavoriteChargingStationIds = favoriteChargingStationList;
    if (!this.isAuthorized) {
      localStorage.setItem("favoriteChargingStationsEM", JSON.stringify(favoriteChargingStationList));
    }
  }

  private updateChargingStationIds(): void {
    if (!this.isAuthorized) {
      localStorage.setItem("disabledChargingStationsEM", JSON.stringify(this.DisabledChargingStationIds));
      localStorage.setItem("favoriteChargingStationsEM", JSON.stringify(this.FavoriteChargingStationIds));
    }
  }

  public setToNormalChargingStation(chargingStationId: string): void {
    const disabledIndex = this.DisabledChargingStationIds.indexOf(chargingStationId);
    if (disabledIndex != -1) {
      this.DisabledChargingStationIds.splice(disabledIndex, 1);
    }
    const favoriteIndex = this.FavoriteChargingStationIds.indexOf(chargingStationId);
    if (favoriteIndex != -1) {
      this.FavoriteChargingStationIds.splice(favoriteIndex, 1);
    }
    this.updateChargingStationIds();
  }

  public setToDisabledChargingStation(chargingStationId: string): void {
    const favoriteIndex = this.FavoriteChargingStationIds.indexOf(chargingStationId);
    if (favoriteIndex != -1) {
      this.FavoriteChargingStationIds.splice(favoriteIndex, 1);
    }
    const disabledIndex = this.DisabledChargingStationIds.indexOf(chargingStationId);
    if (disabledIndex == -1) {
      this.DisabledChargingStationIds.push(chargingStationId);
    }
    this.updateChargingStationIds();
  }

  public setToFavoriteChargingStation(chargingStationId: string): void {
    const disabledIndex = this.DisabledChargingStationIds.indexOf(chargingStationId);
    if (disabledIndex != -1) {
      this.DisabledChargingStationIds.splice(disabledIndex, 1);
    }
    const favoriteIndex = this.FavoriteChargingStationIds.indexOf(chargingStationId);
    if (favoriteIndex == -1) {
      this.FavoriteChargingStationIds.push(chargingStationId);
    }
    this.updateChargingStationIds();
  }

  public addressesJSONtoAddresses(addresses): Address[] {
    const addressesEl: Address[] = addresses.map((address) => {
      let addressEl = new Address(
        address.AddressType === "home" ? AddressType.Home : AddressType.Work,
        address.Description,
        address.Location,
        address.BoundingBox);
      return addressEl;
    });
    return addressesEl;
  }

  public getAddresses(): Address[] {
    return this.Addresses;
  }

  public setAddresses(address: Address[]): void {
    this.Addresses = address;
  }

  public getHomeAddress(): Address | undefined {
    return this.Addresses.find((addressEl) => { return addressEl.AddressType == AddressType.Home });
  }

  public setHomeAddress(address: Address) {
    let index;
    if (this.Addresses.some((addressEl, i) => {
      index = i;
      return addressEl.AddressType === AddressType.Home;
    })) {
      this.Addresses.splice(index, 1);
    }

    this.Addresses.push(address);
  }

  public removeHomeAddress(): void {
    let index;
    if (this.Addresses.some((addressEl, i) => {
      index = i;
      return addressEl.AddressType === AddressType.Home;
    })) {
      this.Addresses.splice(index, 1);
    }
  }

  public getWorkAddress(): Address | undefined {
    return this.Addresses.find((addressEl) => { return addressEl.AddressType == AddressType.Work });
  }

  public setWorkAddress(address: Address): void {
    let index;
    if (this.Addresses.some((addressEl, i) => {
      index = i;
      return addressEl.AddressType === AddressType.Work;
    })) {
      this.Addresses.splice(index, 1);
    }

    this.Addresses.push(address);
  }

  public removeWorkAddress(): void {
    let index;
    if (this.Addresses.some((addressEl, i) => {
      index = i;
      return addressEl.AddressType === AddressType.Work;
    })) {
      this.Addresses.splice(index, 1);
    }
  }

  public getTracking(): boolean {
    return this.Tracking;
  }

  public setTracking(tracking: boolean) {
    this.Tracking = tracking;
    this.ObservableTracking.next(tracking);
  }
}
